import { useRef, type KeyboardEvent } from 'react';
import './ListBox.css';

export interface ListBoxOption<V extends string | number> {
  value: V;
  label: string;
}

export interface ListBoxProps<V extends string | number> {
  options: ReadonlyArray<ListBoxOption<V>>;
  value: V | null;
  onChange: (value: V) => void;
  /** Enter / double-click on an option — e.g. press the dialog's OK. */
  onActivate?: (value: V) => void;
  /** Accessible name (and the caption a form shows above it). */
  ariaLabel: string;
  /** Height in px — XAF's EnumListPropertyEditor uses 150. */
  height?: number;
}

/**
 * The shared single-select LIST BOX — XAF's ASPxListBox (e.g. LDM
 * EnumListPropertyEditor: full width, 150px, one row per option). For a choice
 * the user reads as a LIST rather than as radio buttons.
 *
 * Keyboard (standing rule 14): it is ONE tab stop; ↑/↓ move the selection,
 * Home/End jump, Enter runs `onActivate`. A click selects, a double-click
 * selects and activates. role="listbox" so the shared focus-first-control
 * standard lands on it when a dialog opens.
 */
export function ListBox<V extends string | number>({
  options, value, onChange, onActivate, ariaLabel, height = 150,
}: ListBoxProps<V>) {
  const ref = useRef<HTMLDivElement>(null);
  const index = options.findIndex((o) => o.value === value);

  function move(to: number) {
    if (options.length === 0) return;
    const i = Math.max(0, Math.min(options.length - 1, to));
    onChange(options[i]!.value);
    ref.current?.querySelectorAll<HTMLElement>('[role="option"]')[i]?.scrollIntoView?.({ block: 'nearest' });
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const stop = () => { e.preventDefault(); e.stopPropagation(); };
    if (e.key === 'ArrowDown') { stop(); move(index < 0 ? 0 : index + 1); }
    else if (e.key === 'ArrowUp') { stop(); move(index < 0 ? 0 : index - 1); }
    else if (e.key === 'Home') { stop(); move(0); }
    else if (e.key === 'End') { stop(); move(options.length - 1); }
    else if (e.key === 'Enter' && onActivate && value !== null) { stop(); onActivate(value); }
  }

  return (
    <div
      ref={ref}
      className="ai-listbox"
      role="listbox"
      aria-label={ariaLabel}
      tabIndex={0}
      style={{ height }}
      aria-activedescendant={index >= 0 ? `ai-lb-${String(options[index]!.value)}` : undefined}
      onKeyDown={onKeyDown}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <div
            key={String(o.value)}
            id={`ai-lb-${String(o.value)}`}
            role="option"
            aria-selected={on}
            className={`ai-listbox__opt${on ? ' on' : ''}`}
            onClick={() => onChange(o.value)}
            onDoubleClick={() => { onChange(o.value); onActivate?.(o.value); }}
          >
            {o.label}
          </div>
        );
      })}
    </div>
  );
}
